import { USER_SORT_KEYS, type UserGroupDetail, type UserListItem, type UserSortKey, groupHoldsFacilities, groupHoldsScopes, groupHoldsSports } from "@seagames/contracts";
import { type ColumnDef, DataTable, IconButton, Icons, ListToolbar, Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { usePageBar } from "../../components/shell/page-bar";
import { FacilitiesSection } from "../../components/user-groups/facilities-section";
import { type GroupFormTarget, GroupFormModal } from "../../components/user-groups/group-form-modal";
import { PermissionsSection } from "../../components/user-groups/permissions-section";
import { ScopesSection } from "../../components/user-groups/scopes-section";
import { SectionCard } from "../../components/user-groups/section-card";
import { SportsSection } from "../../components/user-groups/sports-section";
import { ActiveChip, StatusChip, Tag } from "../../components/users/ui";
import { ApiError } from "../../lib/api/client";
import { errorMessage, isTransientError } from "../../lib/api/messages";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import { listSearch } from "../../lib/list-page/list-search";
import { useListPage } from "../../lib/list-page/use-list-page";
import { toast } from "../../lib/ui/toast";
import { deleteUserGroup, invalidateUserGroups, updateUserGroup, userGroupDetailOptions } from "../../lib/user-groups/queries";
import { roleLabel, statusLabel } from "../../lib/users/labels";
import { usersListOptions } from "../../lib/users/queries";

/**
 * One user group, in labelled sections: Summary, Where (scope rows) — or,
 * for a facility group (Transport provider), Which companies — What
 * (permissions), Sports (Operation groups only) and Members. Each section saves
 * on its own with ONE request. ADMIN sees the same page read-only. The
 * members table's page / search / sort live in this page's URL.
 */
export const Route = createFileRoute("/_app/users/groups/$groupId")({
	validateSearch: listSearch(USER_SORT_KEYS),
	component: UserGroupPage,
});

function UserGroupPage() {
	const { t } = useTranslation("userGroups");
	const { t: tc } = useTranslation("common");
	const { session } = Route.useRouteContext();
	const { groupId } = Route.useParams();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const detail = useQuery(userGroupDetailOptions(groupId));

	if (detail.isPending) {
		return (
			<div className="flex flex-col items-center gap-[0.75rem] py-[4rem]">
				<span className="loading loading-spinner loading-md text-base-content/40" />
				<p className="text-sm text-base-content/50">{tc("state.loading")}</p>
			</div>
		);
	}
	if (detail.isError) {
		return (
			// Her `.card.empty`: an icon, a 15px title, one line, the way on.
			<div className="grid justify-items-center gap-[0.5rem] rounded-box border border-gms-line bg-base-100 px-[1rem] py-[2.25rem] text-center text-[0.84375rem] text-gms-ink-2">
				<Icons.Alert aria-hidden className="size-[2rem] text-gms-ink-3" />
				<b className="text-[0.9375rem] font-semibold text-gms-navy-ink">{t("detail.loadFailedTitle")}</b>
				<span>{errorMessage(detail.error)}</span>
				<div className="mt-[0.25rem] flex flex-wrap justify-center gap-[0.5rem]">
					{isTransientError(detail.error) && (
						<button type="button" className="btn btn-sm btn-primary" onClick={() => void detail.refetch()}>
							{tc("action.retry")}
						</button>
					)}
					<Link to="/users/groups" className="btn btn-sm btn-line">
						{t("detail.back")}
					</Link>
				</div>
			</div>
		);
	}

	// Keyed by id so section drafts never leak from one group into the next.
	return <GroupView key={detail.data.user_group_id} group={detail.data} isSuper={isSuper} />;
}

function GroupView({ group, isSuper }: { group: UserGroupDetail; isSuper: boolean }) {
	const { t } = useTranslation("userGroups");
	const { t: tUsers } = useTranslation(["users", "common"]);
	const navigate = useNavigate();
	const [form, setForm] = useState<GroupFormTarget | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [confirmDeactivate, setConfirmDeactivate] = useState(false);
	const [statusBusy, setStatusBusy] = useState(false);
	const role = roleLabel(tUsers, group.role_code);
	// Her rule: the name, what it is and the way back live in the shell's top bar.
	usePageBar({ title: group.name, subtitle: t("detail.subtitle", { role }), back: { to: "/users/groups", label: t("detail.back") } });

	const setActive = async (next: boolean) => {
		setStatusBusy(true);
		try {
			await updateUserGroup(group.user_group_id, { is_active: next });
			await invalidateUserGroups();
			toast.success(next ? t("toast.activated", { name: group.name }) : t("toast.deactivated", { name: group.name }));
			setConfirmDeactivate(false);
		} catch (err) {
			toast.error(err, t("toast.statusFailed"));
		} finally {
			setStatusBusy(false);
		}
	};
	// Turning a group off with people in it takes their access away — say so first. Turning it on, or off while empty, is harmless.
	const onToggle = (next: boolean) => (!next && group.member_count > 0 ? setConfirmDeactivate(true) : void setActive(next));

	const remove = async () => {
		try {
			await deleteUserGroup(group.user_group_id);
		} catch (err) {
			if (err instanceof ApiError && err.code === "CONFLICT") throw new Error(t("errors.hasMembers"));
			throw err;
		}
		toast.success(t("delete.deleted", { name: group.name }));
		await navigate({ to: "/users/groups" });
		await invalidateUserGroups();
	};

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			{/* ── Header: the name is in the top bar; here what it is, and the actions ── */}
			<header className="flex flex-wrap items-center justify-between gap-[0.75rem]">
				<div className="flex min-w-0 flex-col gap-[0.375rem]">
					<div className="flex flex-wrap items-center gap-[0.375rem]">
						<Tag>{role}</Tag>
						<ActiveChip active={group.is_active}>{group.is_active ? t("table.active") : t("table.inactive")}</ActiveChip>
					</div>
					<p className={`max-w-[52rem] text-[0.875rem] ${group.description ? "text-gms-ink-2" : "text-gms-ink-3 italic"}`}>
						{group.description ?? t("detail.noDescription")}
					</p>
				</div>
				{isSuper ? (
					<div className="flex flex-wrap items-center gap-[0.5rem]">
						<span title={group.member_count > 0 ? t("delete.disabledTitle") : undefined}>
							<button type="button" className="btn btn-line btn-sm gap-[0.375rem] text-gms-bad disabled:border-gms-line-2 disabled:bg-base-100 disabled:text-gms-bad disabled:opacity-45" onClick={() => setDeleting(true)} disabled={group.member_count > 0}>
								<Icons.Trash className="size-[1rem]" />
								{t("detail.delete")}
							</button>
						</span>
						<button type="button" className="btn btn-sm btn-primary gap-[0.375rem]" onClick={() => setForm({ mode: "edit", group })}>
							<Icons.Pencil className="size-[1rem]" />
							{t("detail.edit")}
						</button>
					</div>
				) : (
					<p className="text-[0.8125rem] text-gms-ink-3">{t("detail.readOnly")}</p>
				)}
			</header>

			{/* ── Summary ───────────────────────────────────────────────── */}
			<SectionCard id="group-summary" title={t("detail.summary.title")}>
				<dl className="grid gap-x-[2rem] gap-y-[0.875rem] text-[0.84375rem] text-gms-navy-ink sm:grid-cols-2 lg:grid-cols-4">
					<SummaryItem label={t("detail.summary.role")}>
						<span className="font-medium">{role}</span>
						<span className="block text-[0.75rem] leading-snug text-gms-ink-3">{t("detail.summary.roleFixed", { role })}</span>
					</SummaryItem>
					<SummaryItem label={t("detail.summary.status")}>
						{isSuper ? (
							<label className="inline-flex cursor-pointer items-center gap-[0.5rem]">
								<input type="checkbox" className="toggle toggle-sm toggle-primary" checked={group.is_active} disabled={statusBusy} onChange={(e) => onToggle(e.target.checked)} />
								<span className="font-medium">{group.is_active ? t("table.active") : t("table.inactive")}</span>
								{statusBusy && <span className="loading loading-spinner loading-xs" />}
							</label>
						) : (
							<ActiveChip active={group.is_active}>{group.is_active ? t("table.active") : t("table.inactive")}</ActiveChip>
						)}
						{!group.is_active && <span className="mt-[0.25rem] block text-[0.75rem] leading-snug text-gms-warn">{t("detail.summary.inactiveNote")}</span>}
					</SummaryItem>
					<SummaryItem label={t("detail.summary.members")}>
						<MemberCount count={group.member_count} />
					</SummaryItem>
					<SummaryItem label={t("detail.summary.updated")}>
						<span title={formatDateTime(group.updated_at)}>{formatShort(group.updated_at)}</span>
						<span className="block text-[0.75rem] text-gms-ink-3" title={formatDateTime(group.created_at)}>
							{t("detail.summary.createdOn", { stamp: formatShort(group.created_at) })}
						</span>
					</SummaryItem>
				</dl>
			</SectionCard>

			{/* A scoped group says WHERE with scope rows; a facility group (M10: Transport provider) names its companies instead. */}
			{groupHoldsScopes(group.role_code) && <ScopesSection group={group} canEdit={isSuper} />}
			{groupHoldsFacilities(group.role_code) && <FacilitiesSection group={group} canEdit={isSuper} />}

			<div className={`grid items-start gap-[1rem] ${groupHoldsSports(group.role_code) ? "lg:grid-cols-2" : ""}`}>
				<PermissionsSection group={group} canEdit={isSuper} />
				{groupHoldsSports(group.role_code) && <SportsSection group={group} canEdit={isSuper} />}
			</div>

			<MembersSection group={group} />

			{isSuper && <GroupFormModal target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					open={deleting}
					onClose={() => setDeleting(false)}
					onConfirm={remove}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: group.name }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
			{isSuper && confirmDeactivate && (
				<Modal
					open
					onClose={() => setConfirmDeactivate(false)}
					size="sm"
					title={t("detail.deactivate.title")}
					dismissible={!statusBusy}
					footer={
						<ModalActions>
							<button type="button" className="btn btn-line btn-sm" onClick={() => setConfirmDeactivate(false)} disabled={statusBusy}>
								{tUsers("common:action.cancel")}
							</button>
							<button type="button" className="btn btn-warning btn-sm" onClick={() => void setActive(false)} disabled={statusBusy}>
								{statusBusy && <span className="loading loading-spinner loading-xs" />}
								{t("detail.deactivate.confirm")}
							</button>
						</ModalActions>
					}
				>
					<p className="text-[0.875rem] leading-relaxed text-gms-ink-2">
						<Trans
							t={t}
							i18nKey="detail.deactivate.body"
							count={group.member_count}
							values={{ name: group.name, count: group.member_count, role }}
							components={{ strong: <strong className="font-semibold text-gms-navy-ink" /> }}
						/>
					</p>
				</Modal>
			)}
		</div>
	);
}

function SummaryItem({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="min-w-0">
			<dt className="text-[0.78125rem] text-gms-ink-3">{label}</dt>
			<dd className="mt-[0.1875rem]">{children}</dd>
		</div>
	);
}

function MemberCount({ count }: { count: number }) {
	const { formatNumber } = useUiStrings();
	return <span className="font-display text-[1.375rem] leading-[1.05] font-bold text-gms-navy-ink tabular-nums">{formatNumber(count)}</span>;
}

// ── Members ─────────────────────────────────────────────────────────────────

interface MemberFilters extends Record<string, unknown> {
	search: string;
}
const EMPTY_MEMBER_FILTERS: MemberFilters = { search: "" };

/** `GET /users?user_group_id=` — paginated, searched and sorted on the server like the users list. */
function MembersSection({ group }: { group: UserGroupDetail }) {
	const { t } = useTranslation("userGroups");
	const { t: tUsers } = useTranslation(["users", "common"]);
	const navigate = Route.useNavigate();
	const search = Route.useSearch();
	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => usersListOptions({ ...q, user_group_id: group.user_group_id }),
		columnsKey: "user-group-members",
		filters: { empty: EMPTY_MEMBER_FILTERS, fromUrl: (s) => ({ search: s.search ?? "" }), toUrl: (f) => ({ search: f.search.trim() || undefined }) },
		summary: { range: (v) => t("members.summary", v), empty: t("members.empty") },
	});

	const columns: ColumnDef<UserListItem, UserSortKey>[] = [
		{
			key: "name",
			header: t("members.user"),
			sortable: true,
			lockVisible: true,
			minWidth: "13rem",
			cell: (u) => (
				<div className="flex min-w-0 flex-col gap-[0.125rem]">
					<Link to="/users/$id" params={{ id: u.id }} className="truncate text-[0.84375rem] leading-[1.25] font-semibold text-gms-navy-ink hover:text-gms-navy hover:underline">
						{u.name}
					</Link>
					<span className="truncate text-[0.75rem] leading-[1.3] text-gms-ink-3">{u.username}</span>
				</div>
			),
		},
		{
			key: "roles" as UserSortKey,
			header: t("members.roles"),
			minWidth: "10rem",
			cell: (u) => (
				<div className="flex flex-wrap gap-[0.25rem]">
					{u.roles.map((r) => (
						<Tag key={r.code} tone={r.code === group.role_code ? "navy" : "muted"}>
							{roleLabel(tUsers, r.code)}
						</Tag>
					))}
				</div>
			),
		},
		{
			key: "status" as UserSortKey,
			header: t("members.status"),
			width: "8rem",
			cell: (u) => <StatusChip status={u.status}>{statusLabel(tUsers, u.status)}</StatusChip>,
		},
		{
			key: "last_login" as UserSortKey,
			header: t("members.lastSignIn"),
			minWidth: "9rem",
			cell: (u) => (
				<span className="whitespace-nowrap text-gms-ink-2 tabular-nums" title={u.last_login_at ? formatDateTime(u.last_login_at) : undefined}>
					{u.last_login_at ? formatShort(u.last_login_at) : <span className="text-gms-ink-3">{t("members.never")}</span>}
				</span>
			),
		},
	];

	return (
		<SectionCard id="group-members" title={t("members.title")} intro={t("members.intro")}>
			<div className="flex flex-col gap-[0.75rem]">
				<ListToolbar
					search={search.search ?? ""}
					onSearch={(v) => page.patch({ search: v || undefined })}
					searchPlaceholder={t("members.searchLabel")}
					onClearAll={page.clearFilters}
				/>
				<DataTable<UserListItem, UserSortKey>
					{...page.table}
					variant="list"
					columns={columns}
					rowKey={(u) => u.id}
					empty={page.emptyState({
						filtered: { title: t("members.filteredTitle"), description: t("members.filteredDescription") },
						none: { title: t("members.noneTitle"), description: t("members.noneDescription") },
						clearLabel: t("members.clearFilters"),
					})}
					rowActions={(u) => (
						<IconButton label={t("members.viewLabel", { name: u.name })} icon={<Icons.Eye />} variant="row" onClick={() => void navigate({ to: "/users/$id", params: { id: u.id } })} />
					)}
				/>
			</div>
		</SectionCard>
	);
}
